import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  TextField,
  Typography,
  FormHelperText
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';

import ActionSettingsLayout from '../ActionSettingsHeader';
import TagEditor from '../Widgets/TagEditor';
import { useActionSettings } from '../../hooks/useActionSettings';
import { useFolderPicker } from '../useFolderPicker';
import { useFolderRestrictions } from '../useFolderRestrictions';

const normalizeJsonForComparison = (value) => {
  if (!value || typeof value !== 'string') {
    return null;
  }
  try {
    const parsed = JSON.parse(value);
    return JSON.stringify(parsed, null, 2);
  } catch {
    return value.trim();
  }
};

const structureToTemplate = (structure) => {
  if (!structure || typeof structure !== 'object') {
    return '';
  }
  try {
    return JSON.stringify(structure, null, 2);
  } catch {
    return '';
  }
};

const ActionsCreateJsonFile = ({ caseid, tags, settingId, onSaveStatusChange }) => {
  const { t } = useTranslation();

  const [targetPath, setTargetPath] = useState('');
  const [jsonTemplate, setJsonTemplate] = useState('');
  const [prettyPrint, setPrettyPrint] = useState(true);
  const [ensureAscii, setEnsureAscii] = useState(false);
  const [encoding, setEncoding] = useState('utf-8');

  const {
    isEnabled,
    setIsEnabled,
    conditions,
    isSaveDisabled,
    forceRunLoading,
    forceRunResult,
    popUpOpen,
    forceRunWarningOpen,
    forceRunWarningCount,
    handleSave,
    handleForceRun,
    handleForceRunConfirm,
    handleForceRunCancel,
    handleConditionsChange,
    handleCloseStatusWindow,
    updateSaveDisabledState
  } = useActionSettings({
    settingId,
    caseid,
    actionType: 'createjsonfile',
    onSaveStatusChange,
    customComparison: (initial = {}) => {
      const initialTemplate =
        typeof initial.jsonTemplate === 'string'
          ? initial.jsonTemplate
          : structureToTemplate(initial.structure);

      const currentNormalized = normalizeJsonForComparison(jsonTemplate);
      const initialNormalized = normalizeJsonForComparison(initialTemplate);

      return (
        targetPath === (initial.target || '') &&
        currentNormalized === initialNormalized &&
        prettyPrint === (initial.prettyPrint ?? true) &&
        ensureAscii === (initial.ensureAscii ?? false) &&
        encoding === (initial.encoding || 'utf-8')
      );
    },
    customSaveData: () => ({
      target: targetPath,
      jsonTemplate,
      prettyPrint,
      ensureAscii,
      encoding
    }),
    loadDataFunction: (data) => {
      const customData = data.customData || {};
      setTargetPath(customData.target || '');
      if (typeof customData.jsonTemplate === 'string') {
        setJsonTemplate(customData.jsonTemplate);
      } else if (customData.structure) {
        setJsonTemplate(structureToTemplate(customData.structure));
      } else {
        setJsonTemplate('');
      }
      setPrettyPrint(customData.prettyPrint !== undefined ? Boolean(customData.prettyPrint) : true);
      setEnsureAscii(customData.ensureAscii !== undefined ? Boolean(customData.ensureAscii) : false);
      setEncoding(customData.encoding || 'utf-8');
    }
  });

  const { restrictToUserFolder, allowedRoots, displayRoot } = useFolderRestrictions(caseid);

  const { handleBrowseDirectory, FolderBrowserComponent } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    allowFileSelection: false,
    ws: null,
    onFolderSelected: (folderPath) => {
      if (!folderPath) {
        return;
      }
      const cleanFolder = folderPath.replace(/[\\/]+$/, '');
      const separator = cleanFolder.includes('\\') ? '\\' : '/';
      const currentFileName = targetPath.split(/[/\\]/).pop() || 'output.json';
      setTargetPath(`${cleanFolder}${separator}${currentFileName}`);
    }
  });

  useEffect(() => {
    updateSaveDisabledState();
  }, [targetPath, jsonTemplate, prettyPrint, ensureAscii, encoding, updateSaveDisabledState]);

  const validationState = useMemo(() => {
    if (!jsonTemplate.trim()) {
      return { error: true, helper: t('workflow.create_json_file.template_required', 'JSON template is required') };
    }
    try {
      JSON.parse(jsonTemplate);
      return { error: false, helper: '' };
    } catch (error) {
      return {
        error: true,
        helper: t('workflow.create_json_file.parse_error', { error: error.message })
      };
    }
  }, [jsonTemplate, t]);

  return (
    <ActionSettingsLayout
      title={t('createjsonfile', 'Create JSON File')}
      isEnabled={isEnabled}
      onEnabledChange={setIsEnabled}
      isSaveDisabled={isSaveDisabled || validationState.error}
      onSave={handleSave}
      onForceRun={handleForceRun}
      forceRunLoading={forceRunLoading}
      forceRunResult={forceRunResult}
      popUpOpen={popUpOpen}
      onCloseStatusWindow={handleCloseStatusWindow}
      forceRunWarningOpen={forceRunWarningOpen}
      forceRunWarningCount={forceRunWarningCount}
      onForceRunConfirm={handleForceRunConfirm}
      onForceRunCancel={handleForceRunCancel}
      conditions={conditions}
      onConditionsChange={handleConditionsChange}
      tags={tags}
    >
      <Grid item xs={12}>
        <Card sx={{ mb: 2 }}>
          <CardHeader 
            title={t('workflow.create_json_file.target_title', 'Target JSON File')} 
            titleTypographyProps={{ 
              sx: { fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 500 } 
            }}
            sx={{ p: { xs: 1.5, sm: 2 } }}
          />
          <CardContent>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'flex-start' }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block' }}>
                    {t('workflow.create_json_file.target_path', 'File path')}
                  </Typography>
                  <TagEditor
                    caseid={caseid}
                    value={targetPath}
                    onChange={setTargetPath}
                    placeholder="C:\\Exports\\{projectid}\\summary.json"
                    singleLine
                    tags={tags}
                  />
                </Box>
                <Button variant="outlined" onClick={handleBrowseDirectory} sx={{ mt: 3 }}>
                  {t('browse', 'Browse')}
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card sx={{ mb: 3 }}>
          <CardHeader 
            title={t('workflow.create_json_file.template_title', 'JSON Template')} 
            titleTypographyProps={{ 
              sx: { fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 500 } 
            }}
            sx={{ p: { xs: 1.5, sm: 2 } }}
          />
          <CardContent>
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block' }}>
                  {t('workflow.create_json_file.template_label', 'Template (valid JSON)')}
                </Typography>
                <TagEditor
                    caseid={caseid}
                  value={jsonTemplate}
                  onChange={setJsonTemplate}
                  multiline
                  minRows={12}
                  tags={tags}
                />
                <FormHelperText error={validationState.error}>
                  {validationState.error ? validationState.helper : t(
                    'workflow.create_json_file.template_helper',
                    'Enter a valid JSON object. You can use placeholders like {projectid}.'
                  )}
                </FormHelperText>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardHeader 
            title={t('workflow.create_json_file.options_title', 'Output Options')} 
            titleTypographyProps={{ 
              sx: { fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 500 } 
            }}
            sx={{ p: { xs: 1.5, sm: 2 } }}
          />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={prettyPrint}
                      onChange={(event) => setPrettyPrint(event.target.checked)}
                    />
                  }
                  label={t('workflow.create_json_file.pretty_print', 'Pretty print')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={ensureAscii}
                      onChange={(event) => setEnsureAscii(event.target.checked)}
                    />
                  }
                  label={t('workflow.create_json_file.ensure_ascii', 'Ensure ASCII (escape Unicode)')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('workflow.create_json_file.encoding', 'Encoding')}
                  value={encoding}
                  onChange={(event) => setEncoding(event.target.value)}
                  fullWidth
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      <Divider sx={{ my: 2 }} />

      {FolderBrowserComponent}
    </ActionSettingsLayout>
  );
};

export default React.memo(ActionsCreateJsonFile);
